import { isEqual } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { HeaderBidderActiveActive, IHeaderBidder } from '~/src/dash/api/console-api';
import { getDeal } from '~/src/dash/api/deals';
import {
  fetchHeaderBidderIDsBelongingToDeal,
  fetchPendingOffers,
  fetchPlatformConnectionDemandSourceBidders,
  fetchPlatformConnectionOfferHistory,
} from '~/src/dash/apiV2/platform-connect/platform-connect';
import {
  ICustomRenderCellJSXProps,
  IListViewTableDataCell,
} from '~/src/dash/components/tables/ListViewTable/common';
import { TextWithBackground } from '~/src/dash/components/TextWithBackground';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import {
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { useBaseConfig, useBaseStore } from '~/src/dash/views/base/contexts';
import {
  EPlatformConnectStatus,
  IGetPendingOffersResponse,
  IPlatformConnectionWithNamesAndType,
  IPlatformConnectUiParams,
} from '~/src/dash/views/platform-connect/common';
import { formatParams } from '~/src/dash/views/platform-connect/common';
import { PendingOffers } from '~/src/dash/views/platform-connect/PendingOffers';
import { PendingOffersDrawer } from '~/src/dash/views/platform-connect/PendingOffers/PendingOffersDrawer';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

export const PendingOffersContainer = () => {
  const { session, currencySymbol, currencyMultiplier } = useBaseStore();
  const cfg = useBaseConfig().PlatformConnect.PendingOffers;

  const [pendingOffers, setPendingOffers] = useState<IPlatformConnectionWithNamesAndType[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [error, setError] = useState<boolean>(false);
  const [uiParams, setUiParams] = useState<IPlatformConnectUiParams>({});
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [loadState, setLoadState] = useState(LoadingStateLoading);
  const [drawerLoadState, setDrawerLoadState] = useState(LoadingStateLoading);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeRowInfo, setActiveRowInfo] = useState<Record<string, IListViewTableDataCell>>(
    {} as Record<string, IListViewTableDataCell>,
  );
  const [isBuyerFinalAccept, setIsBuyerFinalAccept] = useState(false);
  const [biddersList, setBiddersList] = useState<Record<string, IListItem>>({});
  const [dealName, setDealName] = useState('');

  useEffect(() => {
    if (
      activeRowInfo &&
      activeRowInfo.BuyerPublisherID?.value === session.publisher.ID &&
      activeRowInfo.DemandSource?.value.length > 0
    ) {
      setDrawerLoadState(LoadingStateLoading);
      fetchPlatformConnectionDemandSourceBidders(
        session.publisher.ID,
        activeRowInfo.DemandSource.value,
      )
        .then((bidders: IHeaderBidder[]) => {
          const bidderList: IMenuList = {};
          bidders.forEach((bidder) => {
            bidderList[bidder.ID] = {
              title: bidder.Name,
              selected: false,
              ID: bidder.ID,
              active: bidder.Active === HeaderBidderActiveActive,
            };
          });
          setBiddersList(bidderList);
        })
        .then(() => {
          setDrawerLoadState(LoadingStateHasData);
        })
        .catch(() => {
          unstable_batchedUpdates(() => {
            setError(true);
            setSnackbarMessage('Error retrieving data.');
            setLoadState(LoadingStateNoData);
          });
        });
    } else if (
      activeRowInfo &&
      activeRowInfo.BuyerPublisherID?.value === session.publisher.ID &&
      activeRowInfo.DealID?.value > 0
    ) {
      setDrawerLoadState(LoadingStateLoading);
      fetchHeaderBidderIDsBelongingToDeal(session.publisher.ID, activeRowInfo.DealID.value)
        .then((bidders: IHeaderBidder[]) => {
          const bidderList: IMenuList = {};
          bidders.forEach((bidder) => {
            bidderList[bidder.ID] = {
              title: bidder.Name,
              selected: false,
              ID: bidder.ID,
              active: bidder.Active === HeaderBidderActiveActive,
            };
          });
          setBiddersList(bidderList);
        })
        .then(() => {
          setDrawerLoadState(LoadingStateHasData);
        })
        .catch(() => {
          unstable_batchedUpdates(() => {
            setError(true);
            setSnackbarMessage('Error retrieving data.');
            setLoadState(LoadingStateNoData);
          });
        });
    }
  }, [activeRowInfo]);

  // fetch a list of pending offers
  const onFetchPendingOffers = (params: IPlatformConnectUiParams) => {
    setLoadState(LoadingStateLoading);
    // only update if params has changed
    if (!isEqual(uiParams, params)) {
      updateCfg(params);
      setUiParams(params);
    }
    fetchPendingOffers(session.publisher.ID, formatParams(params))
      .then(({ PendingOffers, TotalRows }: IGetPendingOffersResponse) => {
        const sortedPendingOffers = PendingOffers.sort((a, b) => a.Status - b.Status);
        unstable_batchedUpdates(() => {
          setPendingOffers(sortedPendingOffers || []);
          setTotalRows(TotalRows);
          setLoadState(LoadingStateHasData);
        });
      })
      .catch(() => {
        unstable_batchedUpdates(() => {
          setError(true);
          setSnackbarMessage('Error retrieving data.');
          setLoadState(LoadingStateNoData);
        });
      });
  };

  // update config for state persistence
  const updateCfg = (params: IPlatformConnectUiParams) => {
    if (params.currPage) cfg.defaultState.page = params.currPage;
    if (params.rowsPerPage) cfg.defaultState.rowsPerPage = params.rowsPerPage;
  };

  const handleClickOpenDrawer = (props: ICustomRenderCellJSXProps) => {
    setDrawerLoadState(LoadingStateLoading);
    if (props.row.Type.value === 'Deal Extension') {
      Promise.all([
        getDeal(props.row.DealID.value, session.publisher.ID),
        fetchPlatformConnectionOfferHistory(session.publisher.ID, props.row.ID.value),
      ])
        .then(([deal, history]) => {
          if (props.row.PlatformConnectionOfferHistory) {
            props.row.PlatformConnectionOfferHistory.value = history;
          }
          unstable_batchedUpdates(() => {
            // ---- Deal get OK.
            setDealName(deal.Name);
            setActiveRowInfo(props.row);
          });
        })
        .then(() => {
          unstable_batchedUpdates(() => {
            setDrawerLoadState(LoadingStateHasData);
            setIsModalOpen(true);
          });
        })
        .catch(() => {
          unstable_batchedUpdates(() => {
            setError(true);
            setSnackbarMessage('Error retrieving data.');
            setDrawerLoadState(LoadingStateNoData);
          });
        });
    } else {
      fetchPlatformConnectionOfferHistory(session.publisher.ID, props.row.ID.value)
        .then((history) => {
          if (props.row.PlatformConnectionOfferHistory) {
            props.row.PlatformConnectionOfferHistory.value = history;
          }
        })
        .then(() => {
          setActiveRowInfo(props.row);
        })
        .then(() => {
          unstable_batchedUpdates(() => {
            setDrawerLoadState(LoadingStateHasData);
            setIsModalOpen(true);
          });
        })
        .catch(() => {
          unstable_batchedUpdates(() => {
            setError(true);
            setSnackbarMessage('Error retrieving data.');
            setDrawerLoadState(LoadingStateNoData);
          });
        });
    }
  };

  const handleRenderStatus = (props: ICustomRenderCellJSXProps) => {
    const isBuyer = session.publisher.ID === props.row.BuyerPublisherID.value;
    const isSeller = session.publisher.ID === props.row.SellerPublisherID.value;

    switch (props.row.CustomLeftColumn.value) {
      case EPlatformConnectStatus.PLATFORM_CONNECTION_READY_TO_ACCEPT:
        if (isBuyer) {
          return <TextWithBackground color='green' text='Ready to Accept' />;
        } else if (isSeller) {
          return <TextWithBackground color='green' text='Accepted' />;
        }
      case EPlatformConnectStatus.PLATFORM_CONNECTION_NEW_COUNTER_OFFER:
        const awaitingBuyerResponse =
          props.row.ActionPublisherID.value === props.row.SellerPublisherID.value;
        const awaitingSellerResponse =
          props.row.ActionPublisherID.value === props.row.BuyerPublisherID.value;
        if (isBuyer && awaitingBuyerResponse) {
          return <TextWithBackground color='purple' text='New Counter Offer' />;
        } else if (isBuyer && awaitingSellerResponse) {
          return <TextWithBackground color='purple' text='Counter Offer Sent' />;
        } else if (isSeller && awaitingBuyerResponse) {
          return <TextWithBackground color='purple' text='Counter Offer Sent' />;
        } else if (isSeller && awaitingSellerResponse) {
          return <TextWithBackground color='purple' text='New Counter Offer' />;
        }
      case EPlatformConnectStatus.PLATFORM_CONNECTION_OFFER_SENT:
        if (isBuyer) {
          return <TextWithBackground color='darkGreen' text='Offer Sent' />;
        } else if (isSeller) {
          return <TextWithBackground color='darkGreen' text='New Offer' />;
        }
      case EPlatformConnectStatus.PLATFORM_CONNECTION_OFFER_DECLINED:
        return <TextWithBackground color='red' text='Offer Declined' />;
      case EPlatformConnectStatus.PLATFORM_CONNECTION_OFFER_CANCELED:
        return <TextWithBackground color='maroon' text='Offer Canceled' />;
      case EPlatformConnectStatus.PLATFORM_CONNECTION_OFFER_EXPIRED:
        return <TextWithBackground color='peach' text='Offer Expired' />;
      default:
        return <TextWithBackground color='' text='' />;
    }
  };

  const handleGetCurrencyValue = (row: Record<string, IListViewTableDataCell>) => {
    let currencyValue = '';
    if (row?.ProposedCPM?.value || String(row?.ProposedCPM?.value) === '0') {
      currencyValue =
        currencySymbol +
        (
          parseFloat(String(row?.ProposedCPM?.value).replace(/,/g, '')) * currencyMultiplier
        ).toFixed(2);
    }
    return currencyValue;
  };

  return (
    <>
      <PendingOffers
        fetchPendingOffers={onFetchPendingOffers}
        cfg={cfg}
        updateCfg={updateCfg}
        isActionPending={false}
        pendingOffers={pendingOffers}
        totalRows={totalRows}
        loadState={loadState}
        handleClickOpenDrawer={handleClickOpenDrawer}
        handleRenderStatus={handleRenderStatus}
        handleGetCurrencyValue={handleGetCurrencyValue}
      />
      <PendingOffersDrawer
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        publisher={session.publisher}
        activeRowInfo={activeRowInfo}
        fetchPendingOffers={onFetchPendingOffers}
        isBuyerFinalAccept={isBuyerFinalAccept}
        setIsBuyerFinalAccept={setIsBuyerFinalAccept}
        biddersList={biddersList}
        loadState={drawerLoadState}
        error={error}
        setError={setError}
        setSnackbarMessage={setSnackbarMessage}
        setLoadState={setLoadState}
        dealName={dealName}
        handleGetCurrencyValue={handleGetCurrencyValue}
      />
      <SnackbarView open={!!snackbarMessage} error={error} onClose={() => setSnackbarMessage('')}>
        {snackbarMessage}
      </SnackbarView>
    </>
  );
};
